import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Grid,
  Paper,
  TextField,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import { useLocation, useNavigate } from "react-router-dom";
import { addMonths, format, parse, startOfDay, subMonths } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import useAxiosInstance from "../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../enums/common.enum";
import { errorToast, handleError } from "../../utils/common";
import { CircularProgress, Backdrop } from "@mui/material";

const handleDownload = (jsonData: any, filename = "data.csv") => {
  console.log(jsonData);
  if (!jsonData || jsonData.length === 0) return;

  const transformedData = jsonData.map((row: any) => {
    const formattedRow = { ...row };

    if (row.beneficiary_details && typeof row.beneficiary_details === "object") {
      const details = row.beneficiary_details;

      if (details.payment_mode === "UPI" && details.upi_id) {
        formattedRow.beneficiary_details = `${details.upi_id}`;
      } else if (details.payment_mode === "BANK") {
        const parts = [];
        if (details.account_no) parts.push(`Account No: ${details.account_no}`);
        if (details.ifsc_code) parts.push(`IFSC: ${details.ifsc_code}`);
        if (details.account_type) parts.push(`Type: ${details.account_type}`);
        formattedRow.beneficiary_details = parts.join(" | ");
      } else {
        const formattedDetails = Object.entries(details)
          .map(([key, value]) => `${key}: ${value}`)
          .join(" | ");
        formattedRow.beneficiary_details = formattedDetails;
      }
    } else {
      formattedRow.beneficiary_details = "";
    }

    return formattedRow;
  });

  const headers = Object.keys(transformedData[0]);
  const csvRows = [];

  csvRows.push(headers.join(","));

  transformedData.forEach((row: any) => {
    const values = headers.map((header) => {
      const val = row[header] !== undefined ? row[header] : "";
      const escaped = ("" + val).replace(/"/g, '\\"');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(","));
  });

  const csvString = csvRows.join("\n");

  const blob = new Blob([csvString], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.setAttribute("hidden", "");
  a.setAttribute("href", url);
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const GenerateReports: React.FC = () => {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState<string>("Monthly Report");
  const [fileFormat, setFileFormat] = useState<string>("CSV");
  const [startDate, setStartDate] = useState<any>(subMonths(new Date(), 1));
  const [endDate, setEndDate] = useState<any>(new Date());
  const [purchaseData, setPurchaseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const privateInstance = useAxiosInstance();
  const location = useLocation();
  const params: any = new URLSearchParams(location.search);
  const locationId = params.get("locationId");

  useEffect(() => {
    const getPurchaseData = async () => {
      setLoading(true);
      try {
        const result = await privateInstance.get(
          `${API_VERSION.V3}/suppliers/purchases?location_id=${locationId}`
        );
        if (result?.data?.status === API_RESULT.SUCCESS) {
          setPurchaseData(
            result.data.response?.filter(
              (x: any) =>
                parse(x.invoice_date, "dd-MM-yyyy", new Date()) >=
                  startOfDay(new Date(startDate)) &&
                parse(x.invoice_date, "dd-MM-yyyy", new Date()) <=
                  startOfDay(new Date(endDate))
            )
          );
        } else {
          errorToast(result.data.message);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    getPurchaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View and download purchase reports
      </Typography>

      {/* Purchase Analytics Card */}
      {/* <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pt: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
            Purchase Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            View purchase trends and analyze spending patterns
          </Typography>

          <Box sx={{ height: 300, width: "100%" }}>
            <Line data={chartConfig} options={chartOptions} />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/generate-reports")}
            >
              Generate Report
            </Button>
            <Button variant="outlined" onClick={() => navigate("/reports")}>
              Report History
            </Button>
          </Box>
        </CardContent>
      </Card> */}

      {/* Generate Purchase Report Card */}
      <Card>
        <CardContent sx={{ pt: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
            Generate Purchase Report
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a custom report based on your requirements
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                label="Report Type"
                onChange={(e) => setReportType(e.target.value as string)}
              >
                <MenuItem value="Monthly Report">Monthly Report</MenuItem>
                {/* <MenuItem value="Quarterly Report">Quarterly Report</MenuItem>
                <MenuItem value="Annual Report">Annual Report</MenuItem> */}
              </Select>
            </FormControl>

            <Box sx={{ mb: 1 }}>
              <Typography variant="body2">Date Range</Typography>
              {/* <Button
                variant="outlined"
                fullWidth
                startIcon={<CalendarTodayIcon fontSize="small" />}
                sx={{ justifyContent: "flex-start" }}
              >
                {dateRange}
              </Button> */}
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      inputFormat="dd/MM/yyyy"
                      value={startDate}
                      onChange={(newValue: Date | null) => {
                        setStartDate(newValue);
                        if (
                          addMonths(new Date(String(newValue)), 1) > new Date()
                        ) {
                          setEndDate(new Date());
                        } else {
                          setEndDate(addMonths(new Date(String(newValue)), 1));
                        }
                      }}
                      minDate={new Date("March 15, 2025")}
                      maxDate={new Date()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={{ mt: 1.5 }}
                          name="start_date"
                          InputLabelProps={{
                            required: true,
                          }}
                          autoComplete="off"
                          onKeyDown={(e: any) => {
                            e.preventDefault();
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="End Date"
                    sx={{ mt: 1.5 }}
                    name="end_date"
                    InputLabelProps={{
                      required: true,
                    }}
                    value={format(endDate, "dd/MM/yyyy")}
                    disabled
                  />
                </Grid>
              </Grid>
            </Box>

            <FormControl size="small" fullWidth>
              <InputLabel>File Format</InputLabel>
              <Select
                value={fileFormat}
                label="File Format"
                onChange={(e) => setFileFormat(e.target.value as string)}
              >
                {/* <MenuItem value="PDF">PDF</MenuItem> */}
                <MenuItem value="CSV">CSV</MenuItem>
                {/* <MenuItem value="EXCEL">EXCEL</MenuItem> */}
              </Select>
            </FormControl>

            {/* Report Preview Section */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Report Preview
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f9f9f9" }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Report Type:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight={500} align="right">
                      {reportType}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Date Range:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight={500} align="right">
                      {format(new Date(startDate), "MMM dd, yyyy")} -{" "}
                      {format(new Date(endDate), "MMM dd, yyyy")}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Purchases:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight={500} align="right">
                      {purchaseData?.length}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight={500} align="right">
                      ₹
                      {purchaseData.reduce(
                        (acc, curr: any) => acc + Number(curr.payable_amount),
                        0
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Paid Amount:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight={500} align="right">
                      ₹
                      {purchaseData
                        .filter((x: any) => x.payment_status === "paid")
                        .reduce(
                          (acc, curr: any) => acc + Number(curr.payable_amount),
                          0
                        )}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Pending Amount:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight={500} align="right">
                      ₹
                      {purchaseData
                        .filter((x: any) => x.payment_status === "pending")
                        .reduce(
                          (acc, curr: any) => acc + Number(curr.payable_amount),
                          0
                        )}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            <Button
              variant="contained"
              fullWidth
              startIcon={<GetAppIcon />}
              sx={{
                mt: 1,
                bgcolor: "rgb(167, 139, 250)",
                "&:hover": {
                  bgcolor: "rgb(147, 119, 230)",
                },
              }}
              onClick={() => handleDownload(purchaseData, "report.csv")}
            >
              Download Report
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Backdrop
        open={loading}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress sx={{ color: "#0058ff" }} />
      </Backdrop>
    </Container>
  );
};

export default GenerateReports;
